import type * as THREE from 'three';
import type { SfxName } from '../content/types';
import { clamp, damp } from '../core/math';
import type { Random } from '../core/Random';
import type { Player } from '../entities/Player';
import { buzzTier } from '../state/rules';

export interface StuntDef {
  id: 'backflip' | 'double' | 'hmb';
  name: string;
  presses: number;
  zone: number;
  speed: number;
  flips: number;
  needsBeer?: boolean;
}

export const STUNTS: readonly StuntDef[] = [
  { id: 'backflip', name: 'Backflip', presses: 1, zone: 0.26, speed: 3.0, flips: 1 },
  { id: 'double', name: 'Double Backflip', presses: 2, zone: 0.2, speed: 3.6, flips: 2 },
  { id: 'hmb', name: '"Hold My Beer" Triple', presses: 3, zone: 0.15, speed: 4.2, flips: 3, needsBeer: true },
];

export interface MeterView {
  show(name: string, presses: number): void;
  hide(): void;
  render(
    needle: number,
    zoneCenter: number,
    zoneWidth: number,
    done: number,
    flash: 'hit' | 'miss' | null,
  ): void;
}

type Phase = 'bounce' | 'trick' | 'fall' | 'land' | 'done';

const BOUNCE_TIME = 0.85;
const MAT_Y = 0.8;

export class StuntController {
  active = false;
  private def: StuntDef = STUNTS[0] as StuntDef;
  private phase: Phase = 'bounce';
  private t = 0;
  private bounceT = 0;
  private height = 0.8;
  private hits = 0;
  private pressedThisBounce = false;
  private missed = 0;
  private needleT = 0;
  private zoneCenter = 0.5;
  private zoneWidth = 0.2;
  private jitter = 0;
  private flash: 'hit' | 'miss' | null = null;
  private flashT = 0;
  private center = { x: 0, z: 0 };
  private fallDir = { x: 1, z: 0 };
  private landed = false;
  private onEnd: (result: 'success' | 'fail' | 'chicken') => void = () => undefined;
  private result: 'success' | 'fail' | 'chicken' = 'chicken';

  constructor(
    private readonly player: Player,
    private readonly meter: MeterView,
    private readonly rng: Random,
    private readonly sfx: (n: SfxName) => void,
  ) {}

  start(
    def: StuntDef,
    center: { x: number; z: number },
    buzz: number,
    onEnd: (result: 'success' | 'fail' | 'chicken') => void,
  ): void {
    this.def = def;
    this.center = center;
    this.onEnd = onEnd;
    this.active = true;
    this.phase = 'bounce';
    this.t = 0;
    this.bounceT = 0;
    this.height = 0.7;
    this.hits = 0;
    this.missed = 0;
    this.pressedThisBounce = false;
    const tier = buzzTier(buzz);
    const courage = tier === 'good' || tier === 'buzzed' ? 1.3 : 1;
    this.zoneWidth = def.zone * courage * (tier === 'hammered' ? 0.85 : 1);
    this.jitter = tier === 'hammered' ? 0.08 : tier === 'buzzed' ? 0.03 : 0;
    this.newZone();
    this.player.scripted = true;
    this.player.teleport(center.x, center.z, this.player.facing);
    this.player.rig.can.visible = !!def.needsBeer;
    this.meter.show(def.name, def.presses);
  }

  private newZone(): void {
    this.zoneCenter = this.rng.range(0.22 + this.zoneWidth / 2, 0.78 - this.zoneWidth / 2);
  }

  private get needle(): number {
    const speed = this.def.speed * (1 + this.hits * 0.18);
    const v = 0.5 + 0.5 * Math.sin(this.needleT * speed);
    return clamp(v + Math.sin(this.needleT * 23) * this.jitter, 0, 1);
  }

  /** Call every frame while active. `pressed` = action button pressed this frame. */
  update(dt: number, pressed: boolean, mat: THREE.Object3D | null): void {
    if (!this.active) return;
    this.t += dt;
    if (this.flashT > 0) {
      this.flashT -= dt;
      if (this.flashT <= 0) this.flash = null;
    }
    const rig = this.player.rig;
    const anim = this.player.anim;

    switch (this.phase) {
      case 'bounce': {
        if (pressed && !this.pressedThisBounce) {
          this.pressedThisBounce = true;
          const n = this.needle;
          if (Math.abs(n - this.zoneCenter) <= this.zoneWidth / 2) {
            this.hits++;
            this.flash = 'hit';
            this.flashT = 0.3;
            this.sfx('ding');
            this.newZone();
          } else {
            this.flash = 'miss';
            this.flashT = 0.5;
            this.startFall();
            break;
          }
        }
        this.needleT += dt;
        this.bounceT += dt / BOUNCE_TIME;
        if (this.bounceT >= 1) {
          this.bounceT -= 1;
          this.sfx('boing');
          if (!this.pressedThisBounce && this.t > BOUNCE_TIME * 1.5) this.missed++;
          this.pressedThisBounce = false;
          this.height = 0.9 + this.hits * 0.8;
          if (this.hits >= this.def.presses) {
            this.phase = 'trick';
            this.bounceT = 0;
            this.height = 3.2 + this.def.flips * 0.6;
            this.meter.hide();
            break;
          }
          if (this.missed >= 4) {
            this.result = 'chicken';
            this.finish();
            return;
          }
        }
        const y = MAT_Y + Math.sin(this.bounceT * Math.PI) * this.height;
        anim.update({
          dt,
          time: this.t,
          speed: 0,
          pose: this.def.needsBeer ? 'drink' : 'stand',
          airborne: this.bounceT > 0.1 && this.bounceT < 0.9,
        });
        rig.root.position.set(this.center.x, y, this.center.z);
        if (mat) mat.position.y = MAT_Y - Math.max(0, 0.25 - Math.sin(this.bounceT * Math.PI)) * 0.8;
        this.meter.render(this.needle, this.zoneCenter, this.zoneWidth, this.hits, this.flash);
        break;
      }
      case 'trick': {
        this.bounceT += dt / (BOUNCE_TIME * 1.6);
        const p = clamp(this.bounceT, 0, 1);
        const y = MAT_Y + Math.sin(p * Math.PI) * this.height;
        anim.update({ dt, time: this.t, speed: 0, pose: 'stand', airborne: true });
        const tuck = Math.sin(p * Math.PI);
        rig.kneeL.rotation.x = rig.kneeR.rotation.x = 1.6 * tuck;
        rig.legL.rotation.x = rig.legR.rotation.x = -1.2 * tuck;
        anim.setFlip(-Math.PI * 2 * this.def.flips * smoothFlip(p), 0);
        rig.root.position.set(this.center.x, y, this.center.z);
        if (p >= 1) {
          this.phase = 'land';
          this.t = 0;
          this.sfx('boing');
          this.sfx('airhorn');
          rig.body.rotation.x = 0;
        }
        break;
      }
      case 'fall': {
        const p = clamp(this.t / 0.9, 0, 1);
        const dist = 3.0 * p;
        const y = MAT_Y * (1 - p) + Math.sin(p * Math.PI) * 1.5;
        rig.root.position.set(
          this.center.x + this.fallDir.x * dist,
          Math.max(0, y),
          this.center.z + this.fallDir.z * dist,
        );
        anim.update({ dt, time: this.t, speed: 0, pose: 'fallen' });
        rig.body.rotation.z = damp(rig.body.rotation.z, p * 1.2, 5, dt);
        if (p >= 1 && !this.landed) {
          this.landed = true;
          this.sfx('thud');
        }
        if (p >= 1 && this.t > 2.6) {
          rig.body.rotation.z = 0;
          this.result = 'fail';
          this.finish();
        }
        break;
      }
      case 'land': {
        rig.root.position.set(this.center.x, MAT_Y, this.center.z);
        anim.update({ dt, time: this.t, speed: 0, pose: 'cheer' });
        if (this.t > 1.8) {
          this.result = 'success';
          this.finish();
        }
        break;
      }
      default:
        break;
    }
  }

  private startFall(): void {
    this.phase = 'fall';
    this.t = 0;
    this.landed = false;
    const a = this.rng.range(0, Math.PI * 2);
    this.fallDir = { x: Math.cos(a), z: Math.sin(a) };
    this.meter.hide();
    this.sfx('boing');
    this.sfx('fail');
  }

  private finish(): void {
    this.phase = 'done';
    this.active = false;
    this.meter.hide();
    const rig = this.player.rig;
    rig.body.rotation.set(0, 0, 0);
    rig.can.visible = false;
    // Land beside the trampoline, not inside its collider.
    const out = this.result === 'fail' ? this.fallDir : { x: 0, z: 1 };
    this.player.scripted = false;
    this.player.teleport(this.center.x + out.x * 2.6, this.center.z + out.z * 2.6, this.player.facing);
    this.onEnd(this.result);
  }

  cancel(): void {
    if (!this.active) return;
    this.result = 'chicken';
    this.finish();
  }
}

function smoothFlip(p: number): number {
  const a = clamp((p - 0.12) / 0.76, 0, 1);
  return a * a * (3 - 2 * a);
}
